import { onChildAdded, set } from 'firebase/database';
import AdapterMobileV1 from '~/class/AdapterMobileV1';
import { JobMode } from '~/interface/Job';
import { useMobileV1Toast } from '~/composables/useMobileV1Toast';

// JOB gateway — cashier-side answerer for the legacy `jobs/*` Firebase silo that
// the (unchanged) Java mobile client and legacy web cashier still use.
//
// Contract replicated from Scripts/job/job.gateway.js + jobMobileOrder.java:
//   1. jobs/handshake : phone pushes child `0`, waits for it to become `>= 1`.
//      We flip 0|1 → 2 (auto-clear after 8s). That's the phone's "go ahead".
//   2. jobs/mobile    : phone pushes the order JSON. We dedupe + guard, compute
//      the next Job # live from the jobchain, write the order into the unified
//      jobchain as a JOB (mode=3), then write the order back with read:2 + jobId
//      (the phone's success / Job-ID signal). Node cleared after 8s.
//
// The Job # is computed on the fly from the jobchain (same as the cashier flow,
// usePosCartStore().getJobCurrentQueueNumber) — there is NO RTDB counter.
//
// Only the main cashier runs this (init is called from bootstrap's
// initCashierControllerRuntime, cashier-only — same as pos.mobile-v1).
export const usePosJobGatewayStore = defineStore("job-gateway", () => {

  // in-flight dedupe + stored replies so a phone retry still gets its success
  const processedUniqueIds = new Set<string>()
  const receivedReply = new Map<string, string>()

  const handleHandshake = (snapshot: any) => {
    const val = snapshot.val()
    if (val === 0 || val === 1) {
      set(snapshot.ref, 2)
      setTimeout(() => { set(snapshot.ref, null) }, 8000)
    }
  }

  const handleMobile = async (snapshot: any) => {
    const refPath = snapshot.ref
    const jsonString = snapshot.val()
    if (jsonString == null) return

    let order: any
    try {
      order = JSON.parse(jsonString)
    } catch (e) {
      return
    }

    if (order.void || order.read == 2) return

    // already handled → re-send the stored reply so the phone shows success on retry
    if (order.uniqueid && receivedReply.has(order.uniqueid)) {
      set(refPath, receivedReply.get(order.uniqueid)!)
      setTimeout(() => { set(refPath, null) }, 2500)
      return
    }
    // in-flight (reply not yet stored) → ignore the duplicate
    if (order.uniqueid && processedUniqueIds.has(order.uniqueid)) return

    // Block while the shop is running over a closed business day — same guard as
    // pos.mobile-v1.handleMobileV1. Reject like an unpaired device (remove + warn).
    if (usePosJobsStore().isRunningOverClosedDay()) {
      setTimeout(() => { set(refPath, null) }, 2500)
      useMobileV1Toast().push({
        variant: 'warn',
        text: 'ร้านเปิดข้ามวัน — กรุณาปิดร้านแล้วเปิดใหม่ก่อนรับออเดอร์จากมือถือ',
        duration: 4000,
      })
      return
    }

    // device pairing — reuse the paired map maintained by pos.mobile-v1
    const pairedDevices = usePosMobileV1Store().pariedDevices
    if (!pairedDevices[order.deviceId]) {
      setTimeout(() => { set(refPath, null) }, 2500)
      useMobileV1Toast().push({
        variant: 'warn',
        text: `ออเดอร์จากอุปกรณ์ที่ยังไม่ได้จับคู่ — ${order.deviceId}`,
        duration: 3000,
      })
      return
    }

    if (order.uniqueid) processedUniqueIds.add(order.uniqueid)

    // Compute the next Job # LIVE from the jobchain (count of existing JOB cards
    // + 1) — same source of truth the cashier flow uses. No RTDB counter.
    const jobId = usePosCartStore().getJobCurrentQueueNumber() + 1

    // Build the job card by REUSING the mobile adapter — same create pattern as
    // pos.mobile-v1.handleMobileV1 (AdapterMobileV1 → usePosJobsStore().add) —
    // then promote it to a JOB: a one-shot card keyed by the order's uniqueid so
    // each job stands alone (the Java client sends a shared alias), shown as
    // "Job #<counter>".
    const job: any = new AdapterMobileV1().fromCloudToJobchain(jsonString)
    const jobKey = String(order.uniqueid || jobId)
    job.mode = JobMode.JOB
    job.alias = jobKey
    job.uuid = jobKey
    job.nickName = "Job #" + jobId
    job.jobId = `${job.timestamp}__${jobKey}`
    usePosJobsStore().add(job, true)

    // reply to the phone: read:2 + jobId is its success / Job-ID signal
    order.read = 2
    order.jobId = jobId
    const reply = JSON.stringify(order)
    if (order.uniqueid) receivedReply.set(order.uniqueid, reply)
    set(refPath, reply)

    useMobileV1Toast().push({
      variant: 'success',
      text: `New job — ${job.nickName || job.alias}`,
      duration: 2000,
    })

    setTimeout(() => { set(refPath, null) }, 8000)
  }

  const init = () => {
    const handshakeRef = useRealtimeDatabase().jobHandshakeRef()
    set(handshakeRef, null)
    onChildAdded(handshakeRef, handleHandshake)

    const mobileRef = useRealtimeDatabase().jobMobileRef()
    set(mobileRef, null)
    onChildAdded(mobileRef, handleMobile)
  }

  return {
    init,
  }
})
